import {
  Briefcase,
  Landmark,
  Plane,
  Settings,
  ShoppingBag,
  Stethoscope,
  Utensils,
} from 'lucide-react';
import type { IndustryCategory } from './types';

export const INDUSTRIES: IndustryCategory[] = [
  {
    id: 'financial',
    name: 'Financial Services & Banking',
    icon: Landmark,
    description:
      'Secure, compliance-first banking automation with advanced risk management protocols.',
    colorClass: 'text-emerald-600 bg-emerald-50 border-emerald-200',
    services: [
      {
        title: 'Secure Money Transfer',
        description:
          'Multi-step verification transfers with PIN codes and transaction confirmation.',
        features: ['Account Validation', 'HSBC UK Operations', 'Stock Transfers'],
      },
      {
        title: 'Account Management',
        description:
          'Real-time balance inquiries and credit limit adjustments via guided conversations.',
        features: ['Balance Checks', 'Credit Limit Branching', 'Card Locking/Unlocking'],
      },
      {
        title: 'Payment & Disputes',
        description: 'Credit card bill processing and fraud dispute handling.',
        features: ['Date Scheduling', 'Fraud Complaints', 'Dispute Verification'],
      },
      {
        title: 'Loan & Specialized Products',
        description: 'Complex eligibility logic for loans and exclusive banking tiers.',
        features: ['Branching Eligibility Logic', 'Velcron Account Protocols', 'Quandrex Protocol'],
      },
    ],
  },
  {
    id: 'healthcare',
    name: 'Healthcare & Medical',
    icon: Stethoscope,
    description: 'HIPAA-compliant patient interaction systems with strict privacy boundaries.',
    colorClass: 'text-cyan-600 bg-cyan-50 border-cyan-200',
    services: [
      {
        title: 'Appointment Scheduling',
        description:
          'Intelligent booking system managing time slots, reasoning, and confirmations.',
        features: [
          'Multi-step Journey',
          'Alternative Slot Suggestions',
          'Doctor Specifics (e.g. Prof Xavier)',
        ],
      },
      {
        title: 'Lab Results & Records',
        description: 'Automated result delivery with sensitivity filtering for abnormal results.',
        features: [
          'Normal vs Abnormal Routing',
          'Privacy Compliance',
          'Prescription Data Protection',
        ],
      },
      {
        title: 'Domain Knowledge',
        description: 'Context-aware agents managing office hours and specialization details.',
        features: ['Neurology Specialization', 'Office Info Management', 'Strict Boundaries'],
      },
    ],
  },
  {
    id: 'retail',
    name: 'Retail & E-commerce',
    icon: ShoppingBag,
    description: 'High-volume customer service automation for modern commerce.',
    colorClass: 'text-orange-600 bg-orange-50 border-orange-200',
    services: [
      {
        title: 'Product Search & Sales',
        description: 'Advanced filtering and inventory management for diverse product lines.',
        features: ['Keyword/Price Filtering', 'Pizza Topping Selection', 'Yarn/Fabric Inventory'],
      },
      {
        title: 'Store Operations',
        description: 'Managing specialized warranties and return policies.',
        features: ['"Bug-Free" Warranty Program', '4-Day/4-Hour Return Policy', 'Tech Support'],
      },
      {
        title: 'Order Logistics',
        description: 'Complete tracking visibility from purchase to delivery.',
        features: ['Shipment Status', 'Location Tracking', 'Delivery Method Selection'],
      },
    ],
  },
  {
    id: 'travel',
    name: 'Travel & Hospitality',
    icon: Plane,
    description: 'Seamless booking experiences for flights, hotels, and ground transport.',
    colorClass: 'text-indigo-600 bg-indigo-50 border-indigo-200',
    services: [
      {
        title: 'Flight Booking',
        description: 'Regulatory-compliant booking with age-based class restrictions.',
        features: ['Under-21 Economy Restriction', 'Class Eligibility', 'Multi-passenger Booking'],
      },
      {
        title: 'Accommodation & Rides',
        description: 'Full-service hotel and taxi reservation systems.',
        features: ['Hotel Check-in/out', 'Dynamic Taxi Routing', 'Mid-conversation Changes'],
      },
      {
        title: 'Smart Recommendations',
        description: 'Context-aware travel suggestions based on preferences.',
        features: ['Ground Travel Options', 'Train/Bus Alternatives', 'Constraint Analysis'],
      },
    ],
  },
  {
    id: 'food',
    name: 'Food Service',
    icon: Utensils,
    description: 'Dynamic ordering systems handling complex customizations and inventory.',
    colorClass: 'text-amber-600 bg-amber-50 border-amber-200',
    services: [
      {
        title: 'Restaurant Operations',
        description: 'Menu management with real-time ingredient checking.',
        features: ['Ingredient Availability', 'Alternative Suggestions', 'Customization Logic'],
      },
      {
        title: 'Complex Ordering',
        description: 'Handling multi-faceted orders with mid-stream changes.',
        features: ['Menu Options (Salad/Sandwich)', 'Filling Selection', 'Grocery Price Checking'],
      },
    ],
  },
  {
    id: 'professional',
    name: 'Professional Services',
    icon: Briefcase,
    description: 'Expert-level consulting and technical support automation.',
    colorClass: 'text-slate-600 bg-slate-50 border-slate-200',
    services: [
      {
        title: 'Technical Support',
        description: 'Tiered support systems for cloud software and hardware.',
        features: ['Enterprise vs Standard Tiers', 'Router Reset (PDMM)', 'API Access Management'],
      },
      {
        title: 'Specialized Consulting',
        description: 'Domain-specific advisory services for niche industries.',
        features: ['Regenerative Agriculture', 'Soil Health Advice', 'Insurance Claims Processing'],
      },
    ],
  },
  {
    id: 'general',
    name: 'General Operations',
    icon: Settings,
    description: 'Essential business logic automation for day-to-day efficiency.',
    colorClass: 'text-blue-600 bg-blue-50 border-blue-200',
    services: [
      {
        title: 'Security & Access',
        description: 'Robust authentication and account recovery flows.',
        features: ['Password Reset Workflows', 'Username Verification', 'Sweepstake Validation'],
      },
      {
        title: 'Scheduling & Subs',
        description: 'Managing ongoing customer engagements and services.',
        features: ['Subscription Cancellation', 'Robotic Cleaning Schedules', 'Safety Protocols'],
      },
    ],
  },
];
